import {
  EventTagCategoryGroupArtsCulture,
  EventTagCategoryGroupCommunity,
  EventTagCategoryGroupEntertainment,
  EventTagCategoryGroupFoodDrink,
  EventTagCategoryGroupIdentity,
  EventTagCategoryGroupMusic,
  EventTagCategoryGroupNightlife,
  type EventAttributeCatalogItem,
  type EventAttributeListResponse,
  type EventTagCatalogGroup,
  type EventTagCatalogItem,
  type EventTagCatalogListResponse,
} from '@/lib/api/apiTypes';
import { requestJSON } from '@/lib/api/api';
import {
  normalizeTagsToFetchedCatalog as normalizeTagsToActiveCatalog,
} from '@/features/events/eventMobileHelpers';
export {
  getEntryPriceLabel,
  getEntryPriceValue,
  getFeedEntryBadgeLabel,
  getResolvedFeedEntryBadgeLabel,
  getEventLimitNotice,
  getEventRSVPButtonLabel,
  getEventRSVPState,
  getEntryTypeBadgeLabel,
  getEntryTypeLabel,
  isTicketedEventWithURL,
  isEventRSVPDisabled,
  formatLastEntryLabel,
  getEventFormatLabel,
  normalizeExternalHTTPURL,
  resolveEventEntryDetails,
  shouldShowPrimaryTicketCTA,
  shouldShowSecondaryEventWebsite,
} from '@/features/events/eventMobileHelpers';

export type EventTagValue = string;

export type EventTagGroup = {
  key: string;
  label: string;
  tags: EventTagMeta[];
};

export type EventTagMeta = {
  value: EventTagValue;
  label: string;
  colors: readonly string[];
};

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const RAINBOW_COLORS = ['#e74c3c', '#f39c12', '#f5c518', '#2ecc71', '#4a9eff', '#9b59b6'] as const;

export const EVENT_AGE_RESTRICTIONS = [
  { value: 'all_ages', label: 'All Ages' },
  { value: '18_plus', label: '18+' },
  { value: '21_plus', label: '21+' },
] as const;

const FALLBACK_EVENT_TAG_GROUPS: EventTagGroup[] = [
  {
    key: 'vibe',
    label: 'Vibe',
    tags: [
      { value: 'live_music', label: 'Live Music', colors: ['#4a9eff'] },
      { value: 'dj', label: 'DJ', colors: ['#9b59b6'] },
      { value: 'karaoke', label: 'Karaoke', colors: ['#ff6b9d'] },
      { value: 'comedy', label: 'Comedy', colors: ['#ff8c42'] },
      { value: 'trivia', label: 'Trivia', colors: ['#2ecc71'] },
    ],
  },
  {
    key: 'food-drink',
    label: 'Food & drink',
    tags: [
      { value: 'food', label: 'Food', colors: ['#27ae60'] },
      { value: 'open_bar', label: 'Open Bar', colors: ['#f39c12'] },
      { value: 'food_trucks', label: 'Food Trucks', colors: ['#e67e22'] },
    ],
  },
  {
    key: 'setting',
    label: 'Setting',
    tags: [
      { value: 'rooftop', label: 'Rooftop', colors: ['#3498db'] },
      { value: 'pool_party', label: 'Pool Party', colors: ['#00bcd4'] },
    ],
  },
  {
    key: 'social',
    label: 'Social',
    tags: [
      { value: 'meetup', label: 'Meetup', colors: ['#e74c3c'] },
      { value: 'lgbtq', label: 'LGBT+', colors: RAINBOW_COLORS },
      { value: 'art', label: 'Art', colors: ['#cddc39'] },
      { value: 'sports', label: 'Sports', colors: ['#2196f3'] },
      { value: 'board_games', label: 'Board Games', colors: ['#795548'] },
    ],
  },
] as const;

const FALLBACK_EVENT_ATTRIBUTES: EventAttributeCatalogItem[] = [
  { slug: 'byob', label: 'BYOB', icon: undefined, sort_order: 10 },
  { slug: 'outdoor', label: 'Outdoor', icon: undefined, sort_order: 20 },
  { slug: 'indoor', label: 'Indoor', icon: undefined, sort_order: 30 },
  { slug: 'pet_friendly', label: 'Pet Friendly', icon: undefined, sort_order: 40 },
  { slug: 'kid_friendly', label: 'Kid Friendly', icon: undefined, sort_order: 50 },
  { slug: 'smoking_area', label: 'Smoking Area', icon: undefined, sort_order: 60 },
];

type CachedCatalog<T> = {
  expiresAt: number;
  value: T;
};

let tagsCache: CachedCatalog<EventTagCatalogGroup[]> | null = null;
let attributesCache: CachedCatalog<EventAttributeCatalogItem[]> | null = null;

export const EVENT_TAG_GROUPS = FALLBACK_EVENT_TAG_GROUPS;
export const COLLAPSED_EVENT_TAGS = EVENT_TAG_GROUPS.flatMap((group) => group.tags).slice(0, 4);

const FALLBACK_EVENT_TAG_META_BY_VALUE = new Map(
  FALLBACK_EVENT_TAG_GROUPS.flatMap((group) => group.tags).map((tag) => [tag.value, tag] as const)
);
const FALLBACK_EVENT_ATTRIBUTE_BY_VALUE = new Map(
  FALLBACK_EVENT_ATTRIBUTES.map((attribute) => [attribute.slug, attribute] as const)
);

function getCachedValue<T>(cache: CachedCatalog<T> | null) {
  if (!cache || cache.expiresAt <= Date.now()) {
    return null;
  }
  return cache.value;
}

export async function fetchTags() {
  const cached = getCachedValue(tagsCache);
  if (cached) {
    return cached;
  }

  const payload = await requestJSON<EventTagCatalogListResponse>('/tags');
  tagsCache = {
    expiresAt: Date.now() + FIVE_MINUTES_MS,
    value: payload.groups,
  };
  return payload.groups;
}

export async function fetchAttributes() {
  const cached = getCachedValue(attributesCache);
  if (cached) {
    return cached;
  }

  const payload = await requestJSON<EventAttributeListResponse>('/attributes');
  attributesCache = {
    expiresAt: Date.now() + FIVE_MINUTES_MS,
    value: payload.attributes,
  };
  return payload.attributes;
}

export function getEventTagMeta(value: string) {
  const fetched = getCachedValue(tagsCache)
    ?.flatMap((group) => group.tags)
    .find((tag) => tag.slug === value);
  if (fetched) {
    return toEventTagMeta(fetched);
  }

  return FALLBACK_EVENT_TAG_META_BY_VALUE.get(value);
}

export function getEventTagLabel(value: string) {
  return getEventTagMeta(value)?.label ?? humanizeEventValue(value);
}

export function getEventTagColors(value: string) {
  return getEventTagMeta(value)?.colors ?? ['#7f8c8d'];
}

export function getEventAttributeLabel(value: string) {
  const fetched = getCachedValue(attributesCache)?.find((attribute) => attribute.slug === value);
  if (fetched) {
    return fetched.label;
  }
  const fallback = FALLBACK_EVENT_ATTRIBUTE_BY_VALUE.get(value);
  if (fallback) {
    return fallback.label;
  }
  return humanizeEventValue(value);
}

export function formatEventAgeRestriction(value: string) {
  const match = EVENT_AGE_RESTRICTIONS.find((option) => option.value === value);
  return match?.label ?? 'All Ages';
}

export function getTagGroupLabel(group: EventTagCatalogGroup['category_group']) {
  switch (group) {
    case EventTagCategoryGroupMusic:
      return 'Music';
    case EventTagCategoryGroupFoodDrink:
      return 'Food & drink';
    case EventTagCategoryGroupEntertainment:
      return 'Entertainment';
    case EventTagCategoryGroupArtsCulture:
      return 'Arts & culture';
    case EventTagCategoryGroupNightlife:
      return 'Nightlife';
    case EventTagCategoryGroupCommunity:
      return 'Community';
    case EventTagCategoryGroupIdentity:
      return 'Identity';
    default:
      return humanizeEventValue(group);
  }
}

export function __resetEventCatalogCacheForTests() {
  tagsCache = null;
  attributesCache = null;
}

export function __primeEventTagCatalogCacheForTests(groups: EventTagCatalogGroup[]) {
  tagsCache = {
    expiresAt: Date.now() + FIVE_MINUTES_MS,
    value: groups,
  };
}

export function normalizeTagsToFetchedCatalog(tags: string[], groups?: EventTagCatalogGroup[] | null) {
  return normalizeTagsToActiveCatalog(tags, groups, getCachedValue(tagsCache));
}

function toEventTagMeta(tag: EventTagCatalogItem): EventTagMeta {
  return {
    value: tag.slug,
    label: tag.label,
    colors: tag.color ? [tag.color] : ['#7f8c8d'],
  };
}

function humanizeEventValue(value: string) {
  return value
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
